// src/config/coupang.ts
//
// ┌─────────────────────────────────────────────────────────────────────────┐
// │ 쿠팡 파트너스 설정                                                        │
// │                                                                         │
// │ 파트너스 > 배너/링크 생성 에서 발급받은 값을 그대로 옮겨 적습니다.         │
// │ id 가 0 이거나 트래킹 코드가 비면 해당 영역은 렌더링되지 않습니다.         │
// │ (애드센스 광고와는 완전히 별개로 동작합니다)                              │
// │                                                                         │
// │ ※ COUPANG_DISCLOSURE 는 공정거래위원회 추천·보증 심사지침에 따른 필수     │
// │   문구입니다. 제휴 링크가 보이는 곳에는 반드시 함께 노출해야 하므로       │
// │   삭제하거나 숨기지 마세요.                                              │
// └─────────────────────────────────────────────────────────────────────────┘

/** 대가성 고지 문구 (쿠팡 파트너스가 지정한 문구 원문) */
export const COUPANG_DISCLOSURE =
    '이 포스팅은 쿠팡 파트너스 활동의 일환으로, 이에 따른 일정액의 수수료를 제공받습니다.';

/** 파트너스 트래킹 코드 */
export const COUPANG_TRACKING_CODE: string = 'AF4390069';

export interface CoupangBannerSpec {
    /** 파트너스 배너 ID */
    id: number;
    /** 파트너스가 알려준 template 값 */
    template: string;
    width: string;
    height: string;
}

/**
 * 다이나믹 배너(스크립트 위젯).
 * 파트너스 코드의 new PartnersCoupang.G({...}) 안의 값과 1:1로 대응합니다.
 */
export const COUPANG_BANNERS: Record<'wide' | 'tall' | 'skyscraper' | 'small', CoupangBannerSpec> = {
    /** 가로형 728x90 — 본문 하단(데스크톱) */
    wide: { id: 1025834, template: 'banner', width: '728', height: '90' },
    /** 대형 세로 600x900 — 현재 미사용 */
    tall: { id: 1025837, template: 'banner', width: '600', height: '900' },
    /** 스카이스크래퍼 160x600 — 사이드바(데스크톱) */
    skyscraper: { id: 1025838, template: 'banner', width: '160', height: '600' },
    /** 소형 120x60 — 현재 미사용 */
    small: { id: 1025839, template: 'banner', width: '120', height: '60' },
};

/**
 * iframe 배너 (폭 100% 반응형).
 * 좁은 화면에서는 고정폭 다이나믹 배너 대신 이쪽을 씁니다.
 */
export const COUPANG_IFRAME_BANNERS = {
    /** 폭 100% · 높이 75px — 본문 하단(모바일) */
    wide: { src: 'https://coupa.ng/cpeFSn', height: 75 },
    /** 폭 100% · 높이 44px — 얇은 띠 (현재 미사용) */
    slim: { src: 'https://coupa.ng/cpeFSo', height: 44 },
} as const;

/**
 * 쿠팡 민생지원 배너 (120x240 고정).
 * 정부지원금이라는 사이트 주제와 맞아 사이드바에 노출합니다.
 */
export const COUPANG_MINSAENG_BANNER = {
    src: 'https://coupa.ng/cpeGni',
    width: 120,
    height: 240,
    title: '쿠팡 민생지원',
} as const;

/**
 * 파트너스 링크 생성으로 만든 제휴 링크.
 *
 * 상품명을 확정해 카드로 노출하려면 src/data/coupangPicks.ts 를 쓰고,
 * 여기 있는 링크들은 상세 페이지 배너 아래 텍스트 링크로 돌아가며 노출됩니다.
 * (라벨은 목적지를 단정하지 않는 중립 문구를 씁니다)
 */
export const COUPANG_LINKS: string[] = [
    'https://link.coupang.com/a/gLghtPtc4q',
    'https://link.coupang.com/a/gLgrVnQhem',
    'https://link.coupang.com/a/gLgsJx37Uy',
    'https://link.coupang.com/a/gLgthcW2cS',
];

/**
 * 페이지마다 다른 링크가 걸리도록 문자열(보통 지원금 slug)로 하나를 고릅니다.
 * 같은 페이지에서는 항상 같은 링크가 나와야 하므로 난수를 쓰지 않습니다.
 */
export function pickCoupangLink(seed: string): string | null {
    if (COUPANG_LINKS.length === 0) return null;
    let sum = 0;
    for (let i = 0; i < seed.length; i += 1) sum = (sum + seed.charCodeAt(i)) % 9973;
    return COUPANG_LINKS[sum % COUPANG_LINKS.length];
}

/** 파트너스 영역을 노출할 수 있는 상태인지 */
export const isCoupangEnabled = COUPANG_TRACKING_CODE !== '';
